import { useEffect, useMemo, useRef } from "react";
import { createWidget } from "../../../other/utils/utils.jsx";

/*const normalizeZIndexesOfModals = () => {
  // updating z-indexes of each modal in stacking order
  document.querySelectorAll([".b-float-root > *:not(.b-messagedialog)"]).forEach((d, idx) => {
    d.style.zIndex = 11001 + idx
  })
}*/

/**
 * @description
 * The component is specific to bryntum popup widgets that being created in bryntum way,
 * It will basically hook into the lifecycle of bryntum widget and help us with using it in react JSX way.
 * @param {"swivutaskeditor"|"swivuresourceeditor"|"swivuclientseditor"|"swivutimeeventeditor"|"swivusettingseditor"|string} widgetType
 * @param {boolean} isShow
 * @param {(editor: { editRecord: (record: object, context: object, otherParams: object) => {}, project: any }, otherParams: { isFirstShow: boolean }) => {}} onShow
 * @param {(editor) => {}} onHide
 * @param {(editor: object, otherParams: {isFirstShow: boolean}) => {}} onBeforeShow
 */
const SwivuModal = ({ widgetType, isShow, onShow, onHide, onBeforeShow, setFontSize, rowHeight, displayAt = null }) => {
  const showRef = useRef({ isFirstShow: true });
  const editorPosition = useRef([])

  // useEffect below makes onShow its dependency so useEffect expect the functions to keep the same reference, but since provider of function may not wrap its functions in to useCallback then
  // we need to take care of it
  const onBeforeShowHandler = useRef(onBeforeShow);

  const editor = useMemo(() => createWidget({
    type: widgetType,
    setFontSize,
    rowHeight,
    centered: !displayAt
  }), [widgetType, setFontSize, rowHeight]);

  useEffect(() => {
    if (editor) {
      const destroyOnHide = editor.on({
        beforeShow() {
          if (onBeforeShowHandler.current) onBeforeShowHandler.current(editor, { isFirstShow: showRef.current.isFirstShow });
        },
        hide() {
          onHide(editor);
          showRef.current.isFirstShow = true;
          editorPosition.current = [];
        },
      });

      return () => {
        destroyOnHide();
      };
    }
  }, [editor, onHide]);

  useEffect(() => {
    if (editor) {
      if (isShow) {
        // if editor was open already, then isFirstShow will be False, and editor get be set back to its last position
        // on editor header click bring this editor to front
        // the headerElement may not be available in case, if "title" property not set from config in popup
        if (editor.headerElement) editor.headerElement.onclick = () => {
          if (!editor.isDestroyed) {
            // The `toFront` function here is a custom implementation, The param `true` here implies that the action was performed by user. See `EditorBase` detailed behavior
            editor.toFront(true);
          }
        }
        if (displayAt) {
          editor.showBy({ target: displayAt });
        } else {
          editor.show();
        }
        // setting editor to its original position. showBy is currently not working
        if (!showRef.current.isFirstShow) {
          editor.setXY(...editorPosition.current)
        };
        onShow(editor, { isFirstShow: showRef.current.isFirstShow });
        showRef.current.isFirstShow = false;
      } else {
        editor.hide();
      }
    }

    return () => {
      if (editor.getXY) {
        // if the editor is destroyed on close then editor.getXY may not exist
        editorPosition.current = editor?.getXY();
      }
    }
  }, [editor, isShow, onShow]);

  return null;
}

export default SwivuModal;
